<template>
  <div class="grayBackground">
    <GenerativeAudio @muted="onMuted" @unmuted="onUnmuted" ref="audio"></GenerativeAudio>
    <template v-if="currentLevel >= 0">
      <Exercise color="solid">
        <div class="topLayerLoader" v-if="levelLoading">
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <v-stage ref="stage" :config="stageConfig" @touchmove="handleTouchMove" @touchend="handleTouchend">
          <v-layer ref="layer" @tap="handleTap" @click="handleClick">
          </v-layer>
        </v-stage>
        <BottomCard height="auto">
            <div v-if="finished" class="white bigPadding">
              <div class="title1 text-center light">
                Congratulations! 🎉
              </div>
              <div class="descriptionText verticalPadding text-center light">
                Well done. Show the screenshot to your best friend and share what you are feeling right now.
              </div>
            </div>
            <div v-else class="palette milkWhiteBackground">
              <v-textarea v-if="debug" style="position: absolute;top: 20px;width: 100%;" v-model="logs"></v-textarea>
              <div class="soundControls">
                <div v-if="!muted" class="mute control" @click="mute">
                </div>
                <div v-if="muted" class="unmute control" @click="unmute">

                </div>
              </div>
              <div class="color"
                   @click="selectColor(index)"
                   v-for="(color, index) in this.level.palette"
                   :style="{backgroundColor: color.hex}"
                   v-bind:class="{active: color.hex === currentColor.hex}"
              >
              </div>
          </div>
        </BottomCard>
      </Exercise>
    </template>
    <template v-else>
      <ExerciseWithBottomControls color="solid">
        <div class="title1 text-center bigBottomMargin">Choose a level</div>
        <div class="levels">
          <div class="card shadow d-flex"  v-for="(level, index) in levels" @click="selectLevel(index)">
            <div class="cover mr-6 ml-2">
              <div class="image" v-bind:style="{backgroundImage: `url('${level.image}')`}"></div>
            </div>
            <div class="description verticalPadding">
              <div class="title2 noMargin">{{level.name}}</div>
              <div class="descriptionText verticalPadding">{{level.minutesToComplete}} minutes</div>
              <div class="palettePreview">
                <div class="color" v-for="color in level.palette" :style="{backgroundColor: color.hex}"></div>
              </div>
            </div>
          </div>
        </div>
      </ExerciseWithBottomControls>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { http } from "../util/http.js"
import moment from "moment";
import ExerciseWithBottomControls from "@/components/containers/ExerciseWithBottomControls";
import BackgroundVideo from "@/components/containers/BackgroundVideo";
import InfiniteAudio from "@/components/players/InfiniteAudio";
import LargePrimaryButton from "@/components/buttons/LargePrimaryButton";
import {sleep} from "@/util/sleep";
import {close} from "@/util/close"
import VueKonva from 'vue-konva'
import axios from 'axios'
import Exercise from "@/components/containers/Exercise";
import BottomCard from "@/components/cards/BottomCard";
import GenerativeAudio from "@/components/players/GenerativeAudio";
import makeSigmoid from 'awesome-sigmoid';
import median from 'median';

Vue.use(VueKonva);
Konva.hitOnDragEnabled = true;

const width = window.innerWidth;
const height = window.innerHeight;

export default {
  components: {
    GenerativeAudio,
    BottomCard,
    Exercise,
    ExerciseWithBottomControls,
  },
  data: () => ({
    moment,
    close,
    sigmoid: null,
    muted: false,
    stageConfig: {
      width,
      height,
      draggable: true,
      scaleX: 0.6,
      scaleY: 0.6
    },
    stage: null,
    layer: null,
    lastCenter: null,
    lastDist: 0,
    image: null,
    canvas: null,
    levelLoading: true,
    currentLevel: -1,
    logs: ``,
    debug: false,
    currentLevelLength: 0,
    completeLength: 0,
    colorJustChanged: false,
    currentColor: -1,
    finished: false,
    maxPathLength: -1,
    minPathLength: Infinity,
    averagePathLength: 0,
    totalPathLength: 0,
    medianPathLength: 0,
    pathLengthList: [],
    audio: null,
    levels: [
      {
        name: 'Aussie',
        url: '/exercises/coloring/levels/json/2.json',
        image: `/exercises/coloring/levels/jpg/2.jpg`,
        minutesToComplete: `4`,
        palette: [
          {
            hex: '#B33771',
          },
          {
            hex: '#F97F51',
          },
          {
            hex: '#EAB543',
          },
          {
            hex: '#58B19F',
          },
          {
            hex: '#2ecc71',
          },
          {
            hex: '#CAD3C8',
          },
          {
            hex: '#3B3B98',
          },
          {
            hex: '#ee82ee',
          },
          {
            hex: '#a07060'
          }
        ]
      },
      {
        name: 'American',
        url: '/exercises/coloring/levels/json/1.json',
        image: `/exercises/coloring/levels/jpg/1.jpg`,
        minutesToComplete: 5,
        palette: [
          {
            hex: '#2d3436',
          },
          {
            hex: '#00b894',
          },
          {
            hex: '#fab1a0',
          },
          {
            hex: '#d63031',
          },
          {
            hex: '#e17055',
          },
          {
            hex: '#fdcb6e',
          },
          {
            hex: '#0984e3',
          },
          {
            hex: '#ffeaa7',
          },
          {
            hex: '#B33771',
          },
          {
            hex: '#F97F51',
          },
          {
            hex: '#EAB543',
          },
          {
            hex: '#58B19F',
          },
          {
            hex: '#CAD3C8',
          },
          {
            hex: '#3B3B98',
          },
          {
            hex: '#ee82ee',
          },
          {
            hex: '#a07060'
          }
        ]
      },
      {
        name: 'Chinese',
        url: '/exercises/coloring/levels/json/4.json',
        image: `/exercises/coloring/levels/jpg/4.jpg`,
        minutesToComplete: 11,
        palette: [
          {
            hex: '#ff4757',
          },
          {
            hex: '#ffa502',
          },
          {
            hex: '#ff7f50',
          },
          {
            hex: '#eccc68',
          },
          {
            hex: '#7bed9f',
          },
          {
            hex: '#2ed573',
          },
          {
            hex: '#ced6e0',
          },
          {
            hex: '#70a1ff',
          },
          {
            hex: '#5352ed',
          },
          {
            hex: '#3742fa',
          },
          {
            hex: '#B33771',
          },
          {
            hex: '#F97F51',
          },
          {
            hex: '#EAB543',
          },
          {
            hex: '#58B19F',
          },
          {
            hex: '#CAD3C8',
          },
          {
            hex: '#3B3B98',
          },
          {
            hex: '#ee82ee',
          },
          {
            hex: '#a07060'
          }
        ]
      }
      ,
      {
        name: 'British',
        url: '/exercises/coloring/levels/json/3.json',
        image: `/exercises/coloring/levels/jpg/3.jpg`,
        minutesToComplete: 20,
        palette: [
          {
            hex: '#9c88ff',
          },
          {
            hex: '#e84118',
          },
          {
            hex: '#c23616',
          },
          {
            hex: '#e1b12c',
          },
          {
            hex: '#fbc531',
          },
          {
            hex: '#4cd137',
          },
          {
            hex: '#44bd32',
          },
          {
            hex: '#00a8ff',
          },
          {
            hex: '#0097e6',
          },
          {
            hex: '#273c75',
          },
          {
            hex: '#192a56',
          },
          {
            hex: '#487eb0',
          },
          {
            hex: '#9c88ff',
          },

          {
            hex: '#B33771',
          },
          {
            hex: '#F97F51',
          },
          {
            hex: '#EAB543',
          },
          {
            hex: '#58B19F',
          },
          {
            hex: '#CAD3C8',
          },
          {
            hex: '#3B3B98',
          },
          {
            hex: '#ee82ee',
          },
          {
            hex: '#a07060'
          }
        ]
      }
      ,
      {
        name: 'Indian',
        url: '/exercises/coloring/levels/json/5.json',
        image: `/exercises/coloring/levels/jpg/5.jpg`,
        minutesToComplete: 35,
        palette: [
          {
            hex: '#FC427B',
          },
          {
            hex: '#55E6C1',
          },
          {
            hex: '#25CCF7',
          },
          {
            hex: '#FD7272',
          },
          {
            hex: '#B33771',
          },
          {
            hex: '#82589F',
          },
          {
            hex: '#F97F51',
          },
          {
            hex: '#EAB543',
          },
          {
            hex: '#58B19F',
          },
          {
            hex: '#CAD3C8',
          },
          {
            hex: '#3B3B98',
          },
          {
            hex: '#ee82ee',
          },
          {
            hex: '#a07060'
          },
          {
            hex: '#d63031',
          },
          {
            hex: '#e17055',
          },
          {
            hex: '#fdcb6e',
          },
          {
            hex: '#0984e3',
          },
          {
            hex: '#2d3436',
          },
          {
            hex: '#00b894',
          },
          {
            hex: '#fab1a0',
          },
          {
            hex: '#ffeaa7',
          },
          {
            hex: '#e1b12c',
          },
          {
            hex: '#fbc531',
          },
          {
            hex: '#4cd137',
          },
          {
            hex: '#44bd32',
          },
          {
            hex: '#00a8ff',
          },
          {
            hex: '#0097e6',
          },
          {
            hex: '#273c75',
          },
          {
            hex: '#192a56',
          },
          {
            hex: '#487eb0',
          },
          {
            hex: '#9c88ff',
          },
          {
            hex: '#9c88ff',
          },
          {
            hex: '#d63031',
          },
          {
            hex: '#e17055',
          },
          {
            hex: '#fdcb6e',
          },
          {
            hex: '#0984e3',
          },
          {
            hex: '#2d3436',
          },
          {
            hex: '#00b894',
          },
          {
            hex: '#fab1a0',
          },

        ]
      }
    ]

  }),

  computed: {

    level() {
      return this.levels[this.currentLevel];
    },

  },

  methods: {

    selectColor(index) {
      this.currentColor = this.levels[this.currentLevel].palette[index];
      this.colorJustChanged = true;
    },

    getDistance(p1, p2) {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    },

    getCenter(p1, p2) {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
    },

    selectLevel(index) {
      console.log(`Select leve`, this.levels[index]);
      this.currentLevel = index;
      this.currentColor = this.level.palette[0];
      this.renderLevel();
    },

    fill(element) {
      if (element.attrs.fill === 'black' || element.attrs.fill === '#000000') return;

      console.log(`Start fill event handling`);
      let startHandling = new Date();

      console.log(element);

      const size = this.getSizeByPathLength(element.pathLength);


      this.completeLength += 1;

      element.fill(this.currentColor.hex);
      this.stage.batchDraw();

      setTimeout(() => {

        let startPlaying = new Date();

        if (this.colorJustChanged && Math.random() > 0.7) {
          this.audio.play(size, 2, 100);
          this.colorJustChanged = false;
        }

        if (Math.random() > 0.7) {
          this.audio.play(size, 1, 100);
        } else {
          this.audio.play(size, 0, 100);
        }

        this.logs += `\nSound played ${Date.now() - startPlaying.getTime()}`;

      }, 0);

      console.log(`Recalculate progress`);
      if (this.layer) {
        console.log(this.layer.children);
        if (this.layer.children.length === 0) return false;
        let leftToFill = this.layer.children.filter(children => children.attrs.fill === 'white');
        console.log(leftToFill);
        this.finished = leftToFill.length <= 0;
      }

      this.logs += `\nFill event handled ${Date.now() - startHandling.getTime()}`;

    },

    handleClick(event) {
      this.fill(event.target);
    },

    handleTap(event) {
      this.fill(event.target);
    },

    handleTouchMove(e) {
      console.log(`Touch move`);
      let startHandling = new Date();

      e.evt.preventDefault();
      var touch1 = e.evt.touches[0];
      var touch2 = e.evt.touches[1];

      if (touch1 && touch2) {
        // if the stage was under Konva's drag&drop
        // we need to stop it, and implement our own pan logic with two pointers
        if (this.stage.isDragging()) {
          this.stage.stopDrag();
        }

        var p1 = {
          x: touch1.clientX,
          y: touch1.clientY,
        };
        var p2 = {
          x: touch2.clientX,
          y: touch2.clientY,
        };

        if (!this.lastCenter) {
          this.lastCenter = this.getCenter(p1, p2);
          return;
        }
        var newCenter = this.getCenter(p1, p2);

        var dist = this.getDistance(p1, p2);

        if (!this.lastDist) {
          this.lastDist = dist;
        }

        // local coordinates of center point
        var pointTo = {
          x: (newCenter.x - this.stage.x()) / this.stage.scaleX(),
          y: (newCenter.y - this.stage.y()) / this.stage.scaleX(),
        };

        var scale = this.stage.scaleX() * (dist / this.lastDist);

        this.stage.scaleX(scale);
        this.stage.scaleY(scale);

        // calculate new position of the stage
        var dx = newCenter.x - this.lastCenter.x;
        var dy = newCenter.y - this.lastCenter.y;

        var newPos = {
          x: newCenter.x - pointTo.x * scale + dx,
          y: newCenter.y - pointTo.y * scale + dy,
        };

        this.stage.position(newPos);
        this.stage.batchDraw();

        this.lastDist = dist;
        this.lastCenter = newCenter;
      }

      this.logs += `\nTouch event handled, ${Date.now() - startHandling.getTime()}`;
    },

    getSizeByPathLength(length) {
      let size =  this.sigmoid(length)*1000;
      //let size = (length/this.maxPathLength)*2500;
      let random = Math.min(Math.max(0, Math.random() * 200), 2500);
      console.log(random);
      size += random;
      return size;
    },



    handleTouchend(e) {
      this.lastDist = 0;
      this.lastCenter = null;
    },

    async renderLevel() {
      this.levelLoading = true;

      await sleep(500);

      try {
        const levelRequest = await axios.get(this.level.url);
        const levelData = levelRequest.data;
        this.currentLevelLength = 0;
        this.pathLengthList = [];

        const stage = this.$refs.stage.getNode();
        this.stage = stage;

        const layer = this.$refs.layer.getNode();
        this.layer = layer;

        for (let i = 0; i < levelData.length; i++) {

          let path = new Konva.Path({
            data: levelData[i].data,
            fill: levelData[i].fill,
            id: i,
            name: i.toString(),
            x: (this.stageConfig.width - (this.stageConfig.width * 0.6)) / 2,
            y: this.stageConfig.height - this.stageConfig.width * 1.3,
          });

          if (levelData[i].fill !== 'black') {
            this.currentLevelLength++;
            this.pathLengthList.push(path.pathLength);

            if (path.pathLength > this.maxPathLength) {
              this.maxPathLength = path.pathLength;
              console.log(`Max`, path.pathLength, path)
            }
            if (path.pathLength < this.minPathLength) {
              this.minPathLength = path.pathLength;
              console.log(`Min`, path.pathLength, path)

            }
            this.totalPathLength += path.pathLength;
          }

          layer.add(path);

        }

        this.averagePathLength = this.totalPathLength / this.currentLevelLength;
        this.medianPathLength = median(this.pathLengthList);
        this.sigmoid = makeSigmoid({
          center: this.averagePathLength,
          deviation: 20,
          deviation_output: 0.9,
        });

        layer.batchDraw();
        this.levelLoading = false;

      } catch (e) {
        console.log(`Can't load level data`, e);
        /*alert(`This exercise is not available right now`, e);
        close();*/
      }
    },

    onMuted() {
      this.muted = true;
    },

    onUnmuted() {
      this.muted = false;
    },

    mute() {
      this.audio.mute();
    },

    unmute() {
      this.audio.unmute();
    },



  },


  mounted() {
    this.audio = this.$refs.audio;
  }
}



</script>

<style lang="scss">
@import "src/assets/scss/index";

.levels {
  .image {
    background-size: contain;
    background-position: center;
    height: 8.87500rem;
    width: 8.87500rem;
    margin-right: auto;
  }
}

.palette {
  display: block;
  overflow-x: scroll;
  overflow-y: hidden;
  height: 5.75rem;
  white-space: nowrap;
  padding: {
    top: 1rem;
    left: 1rem;
    bottom: 1rem;
  }
  .color {
    display: inline-block;
    cursor: pointer;
    width: 2.1875rem;
    height: 2.1875rem;
    border-radius: 100%;
    margin: 0.625rem;
    vertical-align: bottom;

    &.active::after {
      position: relative;
      content: " ";
      width: 1.5625rem;
      display: inline-block;
      height: 1.5625rem;
      background: transparent;
      border-radius: 100%;
      border: 0.1875rem solid #fff;
      z-index: 3;
      margin-top: 0.3125rem;
      margin-left: 0.3125rem;
    }
  }
}

.palettePreview {
  height: 1.8rem;
  overflow: hidden;

  .color {
    display: inline-block;
    cursor: pointer;
    width: 1.2rem;
    height: 1.2rem;
    border-radius: 100%;
    margin-right: 0.8rem;
    vertical-align: bottom;
  }
}

.topLayerLoader {
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 30;
  top: 40%;
}

.soundControls {
  display: inline-block;
  padding-right: 1.2rem;
  margin-right: 0.8rem;
  border-right: 1px solid #5b5b5b4d;

  .control {
    width: 2.1875rem;
    height: 2.1875rem;
    background-position: center;
    filter: invert(70%);


  }
  .mute {
    background-image: url('/icons/dark/mute.svg');
  }

  .unmute {
    background-image: url('/icons/dark/unmute.svg');
  }
}


</style>
